import {
  EventLogMetricCategory,
  METRIC_CATEGORY_ORDER,
} from '@chiroup/core/constants/metricCategories';
import React, { useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import useEventLogClinicDashboard from '../../hooks/useEventLogClinicDashboard';
import useUserLevelActivity from '../../hooks/useUserLevelActivity';
import CategoryTabs from './CategoryTabs';
import DateRangePicker, { computeRange, RangePreset } from './DateRangePicker';
import LocationFilterDropdown from './LocationFilterDropdown';
import MetricChartModal from './MetricChartModal';
import MetricDrawer from './MetricDrawer';
import MetricGrid from './MetricGrid';
import SummaryCard from './SummaryCard';
import UserActivitySection from './UserActivitySection';
import UserFilterDropdown from './UserFilterDropdown';

const VALID_PRESETS = new Set<RangePreset>(['7d', '30d', '90d', 'custom']);
const VALID_CATEGORIES = new Set<EventLogMetricCategory>(METRIC_CATEGORY_ORDER);

type Props = {
  clinicId: number;
};

const parseUserIds = (raw: string | null): string[] => {
  if (!raw) return [];

  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

const parseLocationIds = (raw: string | null): number[] => {
  if (!raw) return [];

  return raw
    .split(',')
    .map(s => Number(s.trim()))
    .filter(n => !isNaN(n) && n > 0);
};

const EventLogClinicDetail: React.FC<Props> = ({ clinicId }) => {
  const { createToast } = useContext(ToastContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const rangeFromUrl = (searchParams.get('range') as RangePreset) || '7d';
  const range: RangePreset = VALID_PRESETS.has(rangeFromUrl) ? rangeFromUrl : '7d';
  const tabFromUrl = (searchParams.get('tab') as EventLogMetricCategory) || 'clinical';
  const activeTab: EventLogMetricCategory = VALID_CATEGORIES.has(tabFromUrl)
    ? tabFromUrl
    : 'clinical';
  const userIds = parseUserIds(searchParams.get('users'));
  const userIdsParam = userIds.length ? userIds.sort().join(',') : undefined;
  const locationIds = parseLocationIds(searchParams.get('locations'));
  const locationIdsParam = locationIds.length
    ? locationIds.sort((a, b) => a - b).join(',')
    : undefined;
  const customStart = searchParams.get('start') || undefined;
  const customEnd = searchParams.get('end') || undefined;

  const { startDate, endDate } = useMemo(
    () => computeRange(range, customStart, customEnd),
    [range, customStart, customEnd]
  );

  const [drawerMetric, setDrawerMetric] = useState<string | null>(null);
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);

  const {
    data: dashboard,
    isFetching: dashboardLoading,
    status,
    error,
  } = useEventLogClinicDashboard(clinicId, startDate, endDate, userIdsParam, locationIdsParam);

  const { data: userActivity, isFetching: userActivityLoading } = useUserLevelActivity(
    userIds,
    startDate,
    endDate
  );

  // Show error toast when dashboard query fails
  React.useEffect(() => {
    if (status === 'error' && error) {
      createToast({
        title: 'Error loading dashboard',
        description: (error as Error)?.message || 'Failed to load clinic dashboard data',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  }, [status, error, createToast]);

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(updates)) {
      if (value == null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }

    setSearchParams(next, { replace: false });
  };

  const onChangeRange = (preset: RangePreset, start?: string, end?: string) => {
    if (preset === 'custom' && start && end) {
      updateParams({ range: 'custom', start, end });
    } else {
      updateParams({ range: preset, start: null, end: null });
    }
  };

  const onChangeTab = (next: EventLogMetricCategory) => updateParams({ tab: next });

  const onChangeUsers = (ids: string[]) => {
    updateParams({ users: ids.length ? ids.sort().join(',') : null });
  };

  const onChangeLocations = (ids: number[]) => {
    updateParams({ locations: ids.length ? ids.sort((a, b) => a - b).join(',') : null });
  };

  const filteredUserNames = useMemo(() => {
    if (!userIds.length || !dashboard?.users) return undefined;
    const names = userIds
      .map(uid => dashboard.users.find(u => u.userId === uid)?.userName)
      .filter(Boolean) as string[];

    return names.length ? names : undefined;
  }, [userIds, dashboard?.users]);

  const filteredLocationNames = useMemo(() => {
    if (!locationIds.length || !dashboard?.locations) return undefined;
    const names = locationIds
      .map(lid => dashboard.locations.find(l => l.locationId === lid)?.locationName)
      .filter(Boolean) as string[];

    return names.length ? names : undefined;
  }, [locationIds, dashboard?.locations]);

  const filteredLabel =
    [
      filteredUserNames
        ? filteredUserNames.length <= 2
          ? filteredUserNames.join(', ')
          : `${filteredUserNames.length} users`
        : null,
      filteredLocationNames
        ? filteredLocationNames.length <= 2
          ? filteredLocationNames.join(', ')
          : `${filteredLocationNames.length} locations`
        : null,
    ]
      .filter(Boolean)
      .join(' + ') || undefined;

  return (
    <>
      <div
        className="p-6 pb-24 space-y-6 min-h-screen"
        style={{
          backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <LocationFilterDropdown
            locations={dashboard?.locations || []}
            selectedLocationIds={locationIds}
            onChange={onChangeLocations}
            loading={dashboardLoading && !dashboard?.locations?.length}
          />
          <UserFilterDropdown
            users={dashboard?.users || []}
            selectedUserIds={userIds}
            onChange={onChangeUsers}
            loading={dashboardLoading && !dashboard?.users?.length}
          />
          <DateRangePicker
            active={range}
            customStart={customStart}
            customEnd={customEnd}
            onChange={onChangeRange}
          />
        </div>

        <SummaryCard
          summary={dashboard}
          daily={dashboard}
          loading={dashboardLoading}
          filteredUserName={filteredLabel}
        />

        {userIds.length > 0 && (
          <UserActivitySection data={userActivity} loading={userActivityLoading} />
        )}

        <div>
          <CategoryTabs
            active={activeTab}
            onChange={onChangeTab}
            categoryTotals={
              dashboard?.categoryTotals || {
                schedule: 0,
                clinical: 0,
                billing: 0,
                patients: 0,
                databases: 0,
                clinic_settings: 0,
                user_settings: 0,
                resources: 0,
              }
            }
          />
          <div key={activeTab} className="pt-4 animate-[fadeIn_300ms_ease-out]">
            <MetricGrid
              category={activeTab}
              metrics={dashboard?.metrics || {}}
              previousMetrics={dashboard?.previousMetrics || {}}
              dailyByMetric={dashboard?.dailyByMetric || {}}
              onMetricClick={setDrawerMetric}
              onMetricExpand={setExpandedMetric}
              loading={dashboardLoading && !dashboard}
            />
          </div>
        </div>
      </div>

      <MetricDrawer
        open={!!drawerMetric}
        onClose={() => setDrawerMetric(null)}
        clinicId={clinicId}
        metricKey={drawerMetric}
        startDate={startDate}
        endDate={endDate}
        userId={userIdsParam}
      />

      <MetricChartModal
        open={!!expandedMetric}
        onClose={() => setExpandedMetric(null)}
        metricKey={expandedMetric}
        value={(dashboard?.metrics || {})[expandedMetric || ''] ?? 0}
        previousValue={(dashboard?.previousMetrics || {})[expandedMetric || ''] ?? 0}
        dailySeries={expandedMetric ? (dashboard?.dailyByMetric || {})[expandedMetric] : undefined}
        dates={dashboard?.dates}
        granularity={dashboard?.granularity}
      />
    </>
  );
};

export default EventLogClinicDetail;
